import { createMigration } from './utils';

/**
 * Removes neutral filters from MediaAssets to fix blurriness caused by unnecessary filter application.
 *
 * Even filters with neutral values (e.g. blur = 0) were being applied, causing visual degradation.
 * This migration removes filters that are in their neutral state to improve media quality.
 */

function isNeutralFilter(filter: any): boolean {
    const classType = filter.get('classType');

    switch (classType) {
        case 'AdjustmentFilter':
            return filter.get('brightness') === 1.0 && filter.get('contrast') === 1.0;

        case 'BlurFilter':
            return filter.get('blur') === 0;

        case 'HslAdjustmentFilter':
            return (
                filter.get('hue') === 0 &&
                filter.get('lightness') === 0 &&
                filter.get('saturation') === 0
            );

        case 'OldFilmFilter':
            return (
                filter.get('noise') === 0 &&
                filter.get('noiseSize') === 1 &&
                filter.get('vignetting') === 0 &&
                filter.get('vignettingAlpha') === 1 &&
                filter.get('vignettingBlur') === 0
            );

        case 'DuoToneFilter':
            // Neutral state: black to white mapping (no color change effect)
            // This requires more complex checking of color values, so we'll be conservative
            // and not remove DuoTone filters in this migration
            return false;

        case 'ColorGradingFilter':
            // Neutral state: empty texture URL and zero intensity
            return filter.get('textureUrl') === '' && filter.get('intensity') === 0;

        case 'GreyscaleFilter':
            // GreyscaleFilter is never neutral - it always applies a grayscale effect
            return false;

        default:
            // Unknown filter type - be conservative and don't remove
            return false;
    }
}

function removeNeutralFilters(map: any, filtersPath: Array<string | number>) {
    const filters = map.getIn(filtersPath);
    if (!filters) {
        return;
    }

    const filteredFilters = filters.filter((filter: any) => !isNeutralFilter(filter));

    // Only update if we actually removed filters
    if (filteredFilters.size !== filters.size) {
        map.setIn(filtersPath, filteredFilters);
    }
}

function migrateAsset(map: any, assetPath: Array<string | number>) {
    const asset = map.getIn(assetPath);
    if (!asset) {
        return;
    }

    const classType = asset.get('classType');

    // Only process MediaAssets (including TimeAwareMediaAsset)
    if (classType === 'MediaAsset' || classType === 'TimeAwareMediaAsset') {
        removeNeutralFilters(map, [...assetPath, 'filters']);
    }
}

function migrateChildren(map: any, childrenPath: Array<string | number>) {
    const children = map.getIn(childrenPath);
    if (!children) {
        return;
    }

    children.forEach((_child: any, index: number) => {
        migrateAsset(map, [...childrenPath, index]);
    });
}

export const migrationTo9_235 = createMigration({
    migrateVideo(video) {
        return video.withMutations((map) => {
            // Migrate scene groups
            map.get('sceneGroups').forEach((_sceneGroup: any, i: number) => {
                map.setIn(
                    ['sceneGroups', i],
                    this.migrateSceneGroup(map.getIn(['sceneGroups', i])),
                );
            });

            // Migrate slides/scenes
            map.get('slides').forEach((_scene: any, i: number) => {
                map.setIn(['slides', i], this.migrateScene(map.getIn(['slides', i])));
            });
        });
    },

    migrateSceneGroup(sceneGroup) {
        return sceneGroup.withMutations((map) => {
            // Migrate SceneGroup's mediaAsset if it exists
            migrateAsset(map, ['mediaAsset']);
        });
    },

    migrateScene(scene) {
        return scene.withMutations((map) => {
            // Migrate children in scene
            migrateChildren(map, ['children']);

            // Migrate children in subslides
            const subslides = map.get('subslides');
            if (subslides) {
                subslides.forEach((_subslide: any, sIndex: number) => {
                    migrateChildren(map, ['subslides', sIndex, 'children']);
                });
            }
        });
    },
});
